import {
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import CustomDialog from './CustomDialog'
import { useRef, useState } from 'react'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { useDispatch, useSelector } from 'react-redux'
import CustomDropdown from './CustomDropdown'
import { LoadingPage } from '../models/File'
import { architectureRoles } from '../models/AccessControl'
import { ApproveIcon } from './Svgs'
import { updateBuildingDocumentSelectedPage } from '../actions/construction'
import { BuildingDocumentType } from '../models/Construction'

const documentCategories: { label: string; type: BuildingDocumentType }[] = [
  { label: 'Calculation', type: BuildingDocumentType.Report },
  {
    label: 'Structural Permit Set',
    type: BuildingDocumentType.Structural,
  },
]

const documentsFilter = [
  'Document 1 - XXX',
  'Document 1 - XXX',
  'Document 1 - XXX',
  'Document 1 - XXX',
  'Document 1 - XXX',
]

const pagesFilter = [
  'Architecture',
  'Engineering',
  'Structure',
  'Mechanical',
  'Electrical',
  'Plumbing',
  'Civil',
]

const userSelector = createSelector(
  [(state: RootState) => state.auth],
  (auth) => auth.user,
)

const selectedAssignedProjectSelector = createSelector(
  [(state: RootState) => state.review],
  (review) => review.selectedProject,
)

const selectedProjectSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.selectedProject,
)

const buildingDocumentsSelector = createSelector(
  [(state: RootState) => state.construction],
  (construction) => construction.documents,
)

interface StructuralDocumentsProps {
  isOpen: boolean
  handleApprove: () => void
  handleClose: () => void
}

const StructuralDocumentsComponent = (props: StructuralDocumentsProps) => {
  const dispatch = useDispatch()
  const user = useSelector(userSelector)
  const engineerSelectedProject = useSelector(selectedAssignedProjectSelector)
  const architectSelectedProject = useSelector(selectedProjectSelector)
  const selectedProjectName = user
    ? architectureRoles.includes(user.role.name)
      ? architectSelectedProject?.name
      : engineerSelectedProject?.file.name
    : ''
  const buildingDocuments = useSelector(buildingDocumentsSelector) ?? []
  const calculationDocument = buildingDocuments.find(
    (document) => document.type === BuildingDocumentType.Report,
  )
  const structuralPermitSetDocument = buildingDocuments.find(
    (document) => document.type === BuildingDocumentType.Structural,
  )
  const [selectedDocumentCategory, setSelectedDocumentCategory] = useState(
    documentCategories[0].type,
  )

  const pageRefs = useRef<HTMLDivElement[]>([])

  const selectedPage = (documentCategory: BuildingDocumentType) => {
    if (documentCategory === BuildingDocumentType.Report) {
      return calculationDocument?.file.pages.find(
        (page) => page.id === calculationDocument.selectedPageId,
      )
    }

    if (documentCategory === BuildingDocumentType.Structural) {
      return structuralPermitSetDocument?.file.pages.find(
        (page) => page.id === structuralPermitSetDocument.selectedPageId,
      )
    }
  }

  const handlePageClick = (
    documentCategory: BuildingDocumentType,
    page: LoadingPage,
  ) => {
    const document =
      documentCategory === BuildingDocumentType.Report
        ? calculationDocument
        : structuralPermitSetDocument
    dispatch(
      updateBuildingDocumentSelectedPage({
        fileId: document?.file.id,
        pageId: page.id,
      }),
    )

    if (documentCategory === BuildingDocumentType.Structural) {
      const selectedPageIndex =
        structuralPermitSetDocument?.file.pages.findIndex(
          (p) => p.id === page.id,
        ) ?? -1
      if (selectedPageIndex !== -1 && pageRefs.current[selectedPageIndex]) {
        pageRefs.current[selectedPageIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }
  }

  const downloadFile = () => {
    const file =
      selectedDocumentCategory === BuildingDocumentType.Report
        ? calculationDocument?.file
        : structuralPermitSetDocument?.file
    if (file && file.url) {
      const a = document.createElement('a')
      a.href = file.url
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(file.url)
    }
  }

  return (
    <CustomDialog
      isOpen={props.isOpen}
      title=''
      hideActions={true}
      hideCloseButton={true}
      containerProps={{
        sx: {
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          width: '48%',
        },
      }}
      paperStyle={{
        borderRadius: '0.4rem',
        padding: 0,
        boxShadow: '0px 3px 5px 0px rgba(0, 0, 0, 0.25)',
        margin: '12px',
        width: '100%',
        maxWidth: '100%',
      }}
      handleClose={props.handleClose}
    >
      <Stack width='100%'>
        <Box
          display='flex'
          justifyContent='space-between'
          p='0.75rem'
          sx={{ background: '#F5F5F5' }}
        >
          <ToggleButtonGroup
            size='small'
            color='primary'
            value={selectedDocumentCategory}
            exclusive
            aria-label='Platform'
            sx={{
              flex: 1,
              borderRadius: '4px',
              backgroundColor: '#F2F2F2',
              '.MuiToggleButton-root': {
                margin: 0,
                border: 'none',
                borderRadius: '4px',
                flex: 'none',
              },
            }}
          >
            {documentCategories.map((category, index) => (
              <ToggleButton
                disableRipple
                key={index}
                value={category.type}
                onClick={() => setSelectedDocumentCategory(category.type)}
                sx={{
                  textTransform: 'none',
                  color: '#97999B',
                  fontSize: '0.7rem',
                  backgroundColor: '#fff',
                  width: '8rem',
                  '&:hover': {
                    backgroundColor: '#E5F0FF',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#1705E5',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#1705E5',
                      color: '#fff',
                    },
                  },
                }}
              >
                {category.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box
            sx={{
              display: 'flex',
              gap: '0.5rem',
            }}
          >
            {
              <Button
                disableRipple
                size='small'
                onClick={downloadFile}
                sx={{
                  background: '#BBBCBC',
                  color: '#04002E',
                  textTransform: 'none',
                  fontSize: '0.7rem',
                  px: '1.75rem',
                }}
              >
                Download
              </Button>
            }
            <Button
              disableRipple
              size='small'
              startIcon={
                <ApproveIcon
                  sx={{
                    fontSize: '0.7rem',
                    color: '#fff',
                  }}
                />
              }
              onClick={props.handleApprove}
              sx={{
                background: '#04002E',
                color: '#fff',
                textTransform: 'none',
                fontSize: '0.7rem',
                px: '0.75rem',
              }}
            >
              Approve & Finalize
            </Button>
          </Box>
        </Box>
        <Stack width='100%' gap='0.75rem' p='0.5rem' boxSizing='border-box'>
          <Box
            display='flex'
            justifyContent='space-between'
            p='0.75rem 0.75rem 0 0.75rem'
            alignItems='center'
          >
            <Box display='flex' gap='0.5rem' alignItems='center'>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: '#797979',
                  fontWeight: 500,
                  lineHeight: 'normal',
                }}
              >
                Document Type:
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  width: '16rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#F5F5F5',
                  borderRadius: '0.3rem',
                }}
              >
                <CustomDropdown
                  title={documentsFilter[0]}
                  color='#04002E'
                  buttonStyleProps={{
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '0.3rem 0.6rem',
                  }}
                  titleStyleProps={{
                    fontSize: '0.7rem',
                  }}
                >
                  {documentsFilter.map((filter, index) => (
                    <MenuItem key={index} onClick={() => {}}>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.64rem',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: 'normal',
                          }}
                        >
                          {filter}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </CustomDropdown>
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: '#797979',
                fontWeight: 500,
                lineHeight: 'normal',
              }}
            >
              Project: {selectedProjectName}
            </Typography>
          </Box>
          <Divider flexItem />
          {selectedDocumentCategory === BuildingDocumentType.Report &&
            !!calculationDocument && (
              <Box display='flex' gap='0.5rem' height='46rem'>
                <Box
                  display='flex'
                  flexDirection='column'
                  width='16rem'
                  sx={{ background: '#F5F5F5', borderRadius: '0.4rem' }}
                >
                  <Box
                    width='calc(100% - 1rem)'
                    p='0.5rem'
                    sx={{ borderBottom: '1px solid #E4E4E4' }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: '#797979',
                        fontWeight: 500,
                        lineHeight: 'normal',
                      }}
                    >
                      Object Select:
                    </Typography>
                  </Box>
                  <Stack sx={{ height: '100%', overflowY: 'auto' }}>
                    {calculationDocument.file.pages.map((page, index) => (
                      <Box
                        onClick={() =>
                          handlePageClick(BuildingDocumentType.Report, page)
                        }
                        key={index}
                        sx={{
                          borderBottom: '1px solid #E4E4E4',
                          cursor: 'pointer',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: '#04002E',
                            fontWeight: 500,
                            lineHeight: 'normal',
                            width: 'calc(100% - 1rem)',
                            p: '0.25rem 0.5rem',
                            background:
                              page.id === calculationDocument.selectedPageId
                                ? '#C8DFFF'
                                : 'transparent',
                          }}
                        >
                          Page {page.number}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
                <Box
                  display='flex'
                  flex='1'
                  height='100%'
                  alignItems='center'
                  justifyContent='center'
                >
                  {!selectedPage(BuildingDocumentType.Report)?.isLoading ? (
                    <Box
                      component='img'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      maxHeight='46rem'
                      maxWidth='46rem'
                      src={selectedPage(BuildingDocumentType.Report)?.image}
                      alt='Calculation Page Content'
                    />
                  ) : (
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      height='46rem'
                    >
                      <CircularProgress size={100} sx={{ color: '#1705E5' }} />
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          {selectedDocumentCategory === BuildingDocumentType.Structural &&
            !!structuralPermitSetDocument && (
              <Box display='flex' gap='0.5rem' height='46rem'>
                <Stack
                  display='flex'
                  flexDirection='column'
                  width='16rem'
                  height='calc(100% - 1rem)'
                  rowGap='0.4rem'
                  p='0.5rem'
                  sx={{
                    background: '#F5F5F5',
                    borderRadius: '0.4rem',
                    overflowY: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      width: '60%',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fff',
                      borderRadius: '0.3rem',
                    }}
                  >
                    <CustomDropdown
                      title={pagesFilter[0]}
                      color='#04002E'
                      buttonStyleProps={{
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '0.3rem 0.6rem',
                      }}
                      titleStyleProps={{
                        fontSize: '0.7rem',
                      }}
                    >
                      {pagesFilter.map((filter, index) => (
                        <MenuItem key={index} onClick={() => {}}>
                          <Box
                            sx={{
                              width: '60%',
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.64rem',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: 'normal',
                              }}
                            >
                              {filter}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </CustomDropdown>
                  </Box>
                  {structuralPermitSetDocument.file.pages.map((page, index) =>
                    !page.isLoading ? (
                      <Box
                        key={index}
                        component='img'
                        display='flex'
                        flex={1}
                        alignItems='center'
                        justifyContent='center'
                        borderRadius='0.2rem'
                        maxWidth='100%'
                        height='auto'
                        border={`1px solid ${page.id === selectedPage(BuildingDocumentType.Structural)?.id ? '#1705E5' : '#CFCFCF'}`}
                        boxShadow='0px 2.75px 2.75px 0px #00000040'
                        src={page.image}
                        alt='Structural Permit Set Page Content'
                        onClick={() =>
                          handlePageClick(BuildingDocumentType.Structural, page)
                        }
                      />
                    ) : (
                      <Box
                        key={index}
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                        maxWidth='100%'
                        minHeight='5rem'
                        border={`1px solid ${page.id === selectedPage(BuildingDocumentType.Structural)?.id ? '#1705E5' : '#CFCFCF'}`}
                        boxShadow='0px 2.75px 2.75px 0px #00000040'
                        borderRadius='0.2rem'
                      >
                        <CircularProgress size={40} sx={{ color: '#1705E5' }} />
                      </Box>
                    ),
                  )}
                </Stack>
                <Stack
                  display='flex'
                  flexDirection='column'
                  flex='1'
                  height='calc(100% - 1rem)'
                  gap='0.5rem'
                  p='0.5rem'
                  sx={{
                    background: '#97999B',
                    borderRadius: '0.4rem',
                    overflowY: 'auto',
                  }}
                >
                  {structuralPermitSetDocument.file.pages.map((page, index) => (
                    <Box
                      key={index}
                      ref={(el: HTMLDivElement) =>
                        (pageRefs.current[index] = el)
                      }
                      component='img'
                      display='flex'
                      flex={1}
                      alignItems='center'
                      justifyContent='center'
                      borderRadius='0.2rem'
                      maxWidth='100%'
                      height='auto'
                      border={`1px solid ${page.id === selectedPage(BuildingDocumentType.Structural)?.id ? '#1705E5' : 'transparent'}`}
                      src={page.image}
                      alt='Structural Permit Set Page Content'
                    />
                  ))}
                </Stack>
              </Box>
            )}
        </Stack>
      </Stack>
    </CustomDialog>
  )
}

export default StructuralDocumentsComponent
